import { forwardRef, useCallback, useEffect, useRef, useState, type InputHTMLAttributes } from 'react';

interface DsSearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value?: string;
  onSearch: (value: string) => void;
  debounce?: number;
}

export const DsSearchInput = forwardRef<HTMLDivElement, DsSearchInputProps>(
  ({ value: controlledValue, onSearch, debounce = 300, className = '', ...inputProps }, ref) => {
    const [localValue, setLocalValue] = useState(controlledValue ?? '');
    const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (controlledValue !== undefined) setLocalValue(controlledValue);
    }, [controlledValue]);

    const handleChange = (val: string) => {
      setLocalValue(val);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => onSearch(val), debounce);
    };

    const handleClear = () => {
      setLocalValue('');
      clearTimeout(timerRef.current);
      onSearch('');
      inputRef.current?.focus();
    };

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }, []);

    useEffect(() => {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    return (
      <div ref={ref} className={`ds-search ${className}`}>
        <span className="ds-search-icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
        </span>
        <input
          ref={inputRef}
          className="ds-input"
          type="text"
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          {...inputProps}
        />
        {localValue ? (
          <button className="ds-search-clear" onClick={handleClear} tabIndex={-1} type="button">
            ×
          </button>
        ) : (
          <span className="ds-search-kbd">
            <span className="ds-kbd">{navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl'}F</span>
          </span>
        )}
      </div>
    );
  },
);
